import { OrganizationIntegrationType } from "@dewo/app/graphql/types";
import { Alert, Button, Popconfirm, Space } from "antd";
import * as Colors from "@ant-design/colors";
import React, { FC, useMemo } from "react";
import { ConnectOrganizationToDiscordButton } from "../../integrations/discord/ConnectOrganizationToDiscordButton";
import { ConnectOrganizationToGithubButton } from "../../integrations/github/ConnectOrganizationToGithubButton";
import { CoordinapeToAdminPanelButton } from "../../integrations/coordinape/CoordinapeToAdminPanelButton";
import { useOrganizationIntegrations } from "../hooks";
import { useRunning } from "@dewo/app/util/hooks";
import { useDeleteOrganizationIntegration } from "../../integrations/hooks";
import { ConnectOrganizationToNotionButton } from "../../integrations/notion/ConnectOrganizationToNotionButton";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons";

interface Props {
  organizationId: string;
}

interface IntegrationInformation {
  name: string;
  component: JSX.Element;
}

export const OrganizationIntegrations: FC<Props> = ({ organizationId }) => {
  const integrations = useOrganizationIntegrations(organizationId);
  const types = useMemo(
    () => new Set(integrations?.map((i) => i.type)),
    [integrations]
  );
  const [handleDeleteIntegration, deletingOrganizationIntegration] = useRunning(
    useDeleteOrganizationIntegration()
  );

  const integrationsMapping: Record<
    OrganizationIntegrationType,
    IntegrationInformation
  > = {
    [OrganizationIntegrationType.DISCORD]: {
      name: "Discord",
      component: (
        <ConnectOrganizationToDiscordButton organizationId={organizationId} />
      ),
    },
    [OrganizationIntegrationType.GITHUB]: {
      name: "Github",
      component: (
        <ConnectOrganizationToGithubButton organizationId={organizationId} />
      ),
    },
    [OrganizationIntegrationType.NOTION]: {
      name: "Notion",
      component: (
        <ConnectOrganizationToNotionButton organizationId={organizationId} />
      ),
    },
    [OrganizationIntegrationType.COORDINAPE]: {
      name: "Coordinape",
      component: <CoordinapeToAdminPanelButton />,
    },
  };

  return (
    <Space direction="vertical">
      {(Object.keys(integrationsMapping) as OrganizationIntegrationType[]).map(
        (type) => {
          const integration = integrationsMapping[type];

          if (!types.has(type)) {
            return integration.component;
          } else {
            return (
              <Alert
                key={type}
                message={`Connected with ${integration.name}`}
                type="success"
                showIcon
                action={
                  <Popconfirm
                    icon={
                      <DeleteOutlined style={{ color: Colors.grey.primary }} />
                    }
                    title={`Disconnect ${integration.name}? This will also disconnect it from all your workspaces`}
                    okType="danger"
                    okText="Disconnect"
                    onConfirm={() => {
                      handleDeleteIntegration(type, organizationId);
                    }}
                  >
                    <Button
                      size="small"
                      type="text"
                      className="text-secondary"
                      loading={deletingOrganizationIntegration}
                      icon={<CloseOutlined />}
                    />
                  </Popconfirm>
                }
              />
            );
          }
        }
      )}
    </Space>
  );
};
